import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
    Loader2, CheckCircle2, XCircle, FolderOpen, Users,
    Eye, Pencil, Bookmark, BookmarkPlus
} from 'lucide-react';
import { toast } from 'sonner';

const API = '/api';

export default function JoinCategoryPage() {
    const { shareToken } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading, login } = useAuth();

    const [shareInfo, setShareInfo] = useState(null);
    const [status, setStatus] = useState('loading'); // loading | invite | joining | success | already | error
    const [categoryInfo, setCategoryInfo] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');

    // Step 1: Fetch public share info (no auth needed)
    useEffect(() => {
        const fetchInfo = async () => {
            try {
                const res = await fetch(`${API}/categories/join/${shareToken}/info`);
                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    setErrorMsg(data.detail || 'This share link is invalid or has expired.');
                    setStatus('error');
                    return;
                }
                const data = await res.json();
                setShareInfo(data);
                setStatus('invite');
            } catch (e) {
                console.warn('Failed to load share link:', e);
                setErrorMsg('Could not load share link. Please try again.');
                setStatus('error');
            }
        };
        fetchInfo();
    }, [shareToken]);

    // Step 2: Join the category
    const handleJoin = useCallback(async () => {
        setStatus('joining');
        try {
            const res = await fetch(`${API}/categories/join/${shareToken}`, {
                method: 'POST',
                credentials: 'include',
            });

            if (res.status === 401) {
                // Save return path and trigger login
                sessionStorage.setItem('join_after_login', `/join/${shareToken}`);
                login();
                return;
            }

            const data = await res.json();
            if (!res.ok) {
                setErrorMsg(data.detail || 'Failed to join');
                setStatus('error');
                return;
            }

            setCategoryInfo(data);
            setStatus(data.already_member ? 'already' : 'success');
            // Auto-redirect to the category after a brief success flash
            if (!data.already_member) {
                setTimeout(() => {
                    navigate(`/dashboard?category=${data.category_id}`);
                }, 1500);
            }
        } catch (e) {
            console.warn('Failed to join category:', e);
            setErrorMsg('Something went wrong. Please try again.');
            setStatus('error');
        }
    }, [shareToken, login, navigate]);

    // Auto-join if user is already logged in and arrives from a redirect
    useEffect(() => {
        if (!authLoading && user && status === 'invite') {
            // Check if we got here from post-login redirect
            const wasRedirected = sessionStorage.getItem('join_after_login');
            if (wasRedirected) {
                sessionStorage.removeItem('join_after_login');
                handleJoin();
            }
        }
    }, [authLoading, user, status, handleJoin]);

    const handleSignInAndJoin = () => {
        sessionStorage.setItem('join_after_login', `/join/${shareToken}`);
        login();
    };

    const handleGoToCategory = () => {
        if (categoryInfo?.category_id) {
            navigate(`/dashboard?category=${categoryInfo.category_id}`);
        } else {
            navigate('/dashboard');
        }
    };

    const permissionLabel = shareInfo?.default_permission === 'edit' ? 'view & add bookmarks' : 'view bookmarks';

    return (
        <div className="min-h-screen flex items-center justify-center px-4" style={{ backgroundColor: '#fafaf8' }}>
            <div className="w-full max-w-sm" data-testid="join-page">

                {/* Loading */}
                {status === 'loading' && (
                    <div className="text-center space-y-3">
                        <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" />
                        <p className="text-sm text-muted-foreground">Loading invitation...</p>
                    </div>
                )}

                {/* Invitation Card */}
                {(status === 'invite' || status === 'joining') && shareInfo && (
                    <div className="bg-white rounded-2xl shadow-lg border border-border/50 overflow-hidden" data-testid="join-invite-card">
                        {/* Header */}
                        <div className="px-6 pt-6 pb-4 text-center">
                            <div className="flex items-center justify-center gap-2 mb-4">
                                <Bookmark className="w-5 h-5 text-primary" />
                                <span className="font-semibold text-base tracking-tight">BrainSync</span>
                            </div>

                            {/* Owner avatar */}
                            <div className="flex justify-center mb-3">
                                {shareInfo.owner_picture ? (
                                    <img
                                        src={shareInfo.owner_picture}
                                        alt={shareInfo.owner_name}
                                        className="w-14 h-14 rounded-full object-cover ring-2 ring-primary/10"
                                        referrerPolicy="no-referrer"
                                    />
                                ) : (
                                    <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center text-xl font-semibold text-primary">
                                        {(shareInfo.owner_name || '?')[0].toUpperCase()}
                                    </div>
                                )}
                            </div>

                            <p className="text-sm text-muted-foreground">
                                <strong className="text-foreground">{shareInfo.owner_name}</strong> invited you to join
                            </p>

                            {/* Category name */}
                            <h1 className="text-2xl font-bold mt-2 tracking-tight" data-testid="join-category-name">
                                {shareInfo.category_name}
                            </h1>
                        </div>

                        {/* Stats */}
                        <div className="flex border-t border-b border-border/50 divide-x divide-border/50">
                            <div className="flex-1 py-3 text-center">
                                <div className="flex items-center justify-center gap-1 text-muted-foreground">
                                    <BookmarkPlus className="w-3.5 h-3.5" />
                                    <span className="text-sm font-semibold text-foreground">{shareInfo.bookmark_count}</span>
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-0.5">bookmarks</p>
                            </div>
                            <div className="flex-1 py-3 text-center">
                                <div className="flex items-center justify-center gap-1 text-muted-foreground">
                                    <Users className="w-3.5 h-3.5" />
                                    <span className="text-sm font-semibold text-foreground">{shareInfo.member_count}</span>
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-0.5">members</p>
                            </div>
                            <div className="flex-1 py-3 text-center">
                                <div className="flex items-center justify-center gap-1 text-muted-foreground">
                                    {shareInfo.default_permission === 'edit' ? (
                                        <Pencil className="w-3.5 h-3.5" />
                                    ) : (
                                        <Eye className="w-3.5 h-3.5" />
                                    )}
                                    <span className="text-sm font-semibold text-foreground capitalize">{shareInfo.default_permission}</span>
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-0.5">access</p>
                            </div>
                        </div>

                        {/* Action */}
                        <div className="px-6 py-5 space-y-3">
                            <p className="text-xs text-center text-muted-foreground">
                                You'll be able to {permissionLabel} in this category.
                            </p>

                            {user ? (
                                <Button
                                    onClick={handleJoin}
                                    disabled={status === 'joining'}
                                    className="w-full h-11"
                                    data-testid="join-btn"
                                >
                                    {status === 'joining' ? (
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    ) : (
                                        <Users className="w-4 h-4 mr-2" />
                                    )}
                                    {status === 'joining' ? 'Joining...' : 'Join collection'}
                                </Button>
                            ) : (
                                <Button
                                    onClick={handleSignInAndJoin}
                                    disabled={authLoading}
                                    className="w-full h-11"
                                    data-testid="sign-in-join-btn"
                                >
                                    <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                                        <path fill="#fff" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                                        <path fill="#fff" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                                        <path fill="#fff" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                                        <path fill="#fff" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                                    </svg>
                                    Sign in with Google to join
                                </Button>
                            )}
                        </div>
                    </div>
                )}

                {/* Success */}
                {status === 'success' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-border/50 p-6 text-center space-y-4" data-testid="join-success">
                        <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto">
                            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">You're in!</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                You've joined <strong>{categoryInfo?.category_name || shareInfo?.category_name}</strong>
                            </p>
                            {(categoryInfo?.owner_name || shareInfo?.owner_name) && (
                                <p className="text-xs text-muted-foreground mt-0.5">
                                    Shared by {categoryInfo?.owner_name || shareInfo?.owner_name}
                                </p>
                            )}
                        </div>
                        <Button onClick={handleGoToCategory} className="w-full h-11" data-testid="go-to-category-btn">
                            <FolderOpen className="w-4 h-4 mr-2" />
                            Open collection
                        </Button>
                    </div>
                )}

                {/* Already a member */}
                {status === 'already' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-border/50 p-6 text-center space-y-4" data-testid="join-already">
                        <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mx-auto">
                            <Users className="w-8 h-8 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Already a member</h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                You're already in <strong>{categoryInfo?.category_name || shareInfo?.category_name}</strong>
                            </p>
                        </div>
                        <Button onClick={handleGoToCategory} className="w-full h-11" data-testid="go-to-category-btn">
                            <FolderOpen className="w-4 h-4 mr-2" />
                            Open collection
                        </Button>
                    </div>
                )}

                {/* Error */}
                {status === 'error' && (
                    <div className="bg-white rounded-2xl shadow-lg border border-border/50 p-6 text-center space-y-4" data-testid="join-error">
                        <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                            <XCircle className="w-8 h-8 text-red-500" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold">Unable to Join</h2>
                            <p className="text-sm text-muted-foreground mt-1">{errorMsg}</p>
                        </div>
                        <Button variant="outline" onClick={() => navigate('/')} data-testid="go-home-btn">
                            Go to BrainSync
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
